import axiosInstance from '../../services/axiosInstance';
import { ICategory } from '../../types/damTypes';
import {
  AiImageArchiveOutputsPage,
  AiImageGeneration,
  AiImageMutationResult,
  AiImagePresetInput,
  AiImagePromptSetting,
  AiImagePromptSettingInput,
  AiImagePublishSelection,
  AiImagePreset,
  AiImageStudioConfig,
  DamSearchAssetResult,
  GenerateAiImagePayload,
  ProductReferenceLookupResult,
  RecipeSearchAssetResult,
} from './types';

export interface SalesSheetCopyProductPayload {
  productName?: string;
  articleNumber?: string;
  ean?: string;
  netContent?: string;
  marketingMessage?: string;
  price?: string;
}

export interface SalesSheetCopyPayload {
  templateName?: string;
  productCount?: number;
  retailerLine?: string;
  speechBubble?: string;
  headlineHint?: string;
  ingressHint?: string;
  uspHints?: string[];
  products: SalesSheetCopyProductPayload[];
}

export interface SalesSheetCopyResult {
  headline: string;
  ingress: string;
  usps: string[];
}

export interface FsAdCopyPayload {
  templateName?: string;
  headlineHint?: string;
  ingressHint?: string;
  products: SalesSheetCopyProductPayload[];
}

export interface FsAdCopyResult {
  headline: string;
  ingress: string;
}

export async function fetchAiImageStudioConfig(): Promise<AiImageStudioConfig> {
  const { data } = await axiosInstance.get<AiImageStudioConfig>('/api/ai-images/config');
  return data;
}

export async function fetchAiImagePresets(scope?: 'general' | 'recipe' | 'wine'): Promise<AiImagePreset[]> {
  const { data } = await axiosInstance.get<AiImagePreset[]>('/api/ai-images/presets', {
    params: { scope },
  });
  return data;
}

export async function fetchAllAiImagePresets(): Promise<AiImagePreset[]> {
  const { data } = await axiosInstance.get<AiImagePreset[]>('/api/ai-images/presets/all');
  return data;
}

export async function createAiImagePreset(payload: AiImagePresetInput): Promise<AiImagePreset> {
  const { data } = await axiosInstance.post<AiImagePreset>('/api/ai-images/presets', payload);
  return data;
}

export async function updateAiImagePreset(presetId: string, payload: AiImagePresetInput): Promise<AiImagePreset> {
  const { data } = await axiosInstance.put<AiImagePreset>(`/api/ai-images/presets/${presetId}`, payload);
  return data;
}

export async function deleteAiImagePreset(presetId: string): Promise<{ deleted: boolean; presetId: string }> {
  const { data } = await axiosInstance.delete<{ deleted: boolean; presetId: string }>(`/api/ai-images/presets/${presetId}`);
  return data;
}

export async function fetchAiImagePromptSettings(): Promise<AiImagePromptSetting[]> {
  const { data } = await axiosInstance.get<AiImagePromptSetting[]>('/api/ai-images/prompt-settings');
  return data;
}

export async function updateAiImagePromptSetting(
  key: string,
  payload: AiImagePromptSettingInput
): Promise<AiImagePromptSetting> {
  const { data } = await axiosInstance.put<AiImagePromptSetting>(`/api/ai-images/prompt-settings/${key}`, payload);
  return data;
}

export async function fetchAiImageGenerations(params?: Record<string, string | number | undefined>): Promise<AiImageGeneration[]> {
  const { data } = await axiosInstance.get<AiImageGeneration[]>('/api/ai-images', { params });
  return data;
}

export async function fetchAiImageArchiveOutputs(params?: Record<string, string | number | undefined>): Promise<AiImageArchiveOutputsPage> {
  const { data } = await axiosInstance.get<AiImageArchiveOutputsPage>('/api/ai-images/archive/outputs', { params });
  return data;
}

export async function fetchAiImageGeneration(id: string): Promise<AiImageGeneration> {
  const { data } = await axiosInstance.get<AiImageGeneration>(`/api/ai-images/${id}`);
  return data;
}

export async function generateAiImages(payload: GenerateAiImagePayload): Promise<AiImageGeneration> {
  const { data } = await axiosInstance.post<AiImageGeneration>('/api/ai-images/generate', payload);
  return data;
}

export async function generateSalesSheetCopy(payload: SalesSheetCopyPayload): Promise<SalesSheetCopyResult> {
  const { data } = await axiosInstance.post<SalesSheetCopyResult>('/api/ai-images/sales-sheet/copy', payload);
  return data;
}

export async function generateFsAdCopy(payload: FsAdCopyPayload): Promise<FsAdCopyResult> {
  const { data } = await axiosInstance.post<FsAdCopyResult>('/api/ai-images/fs-ad/copy', payload);
  return data;
}

export async function searchAiImageDamAssets(query: string, categoryId?: string): Promise<DamSearchAssetResult[]> {
  const { data } = await axiosInstance.get<{ assets: DamSearchAssetResult[] }>('/api/ai-images/dam/search', {
    params: { q: query, category: categoryId || undefined },
  });
  return data.assets;
}

export async function fetchAiImageDamAsset(assetId: string): Promise<DamSearchAssetResult> {
  const { data } = await axiosInstance.get<{ asset: DamSearchAssetResult }>(`/api/ai-images/dam/${assetId}`);
  return data.asset;
}

export async function searchAiImageRecipeAssets(query: string): Promise<RecipeSearchAssetResult[]> {
  const { data } = await axiosInstance.get<{ assets: RecipeSearchAssetResult[] }>('/api/ai-images/recipes/search', {
    params: { q: query },
  });
  return data.assets;
}

export async function fetchAiImageRecipeAsset(assetId: string): Promise<RecipeSearchAssetResult> {
  const { data } = await axiosInstance.get<{ asset: RecipeSearchAssetResult }>(`/api/ai-images/recipes/${assetId}`);
  return data.asset;
}

export async function lookupAiImageProductReference(sku: string): Promise<ProductReferenceLookupResult> {
  const { data } = await axiosInstance.get<ProductReferenceLookupResult>(`/api/ai-images/products/${encodeURIComponent(sku)}`);
  return data;
}

export async function fetchAiImageCategories(): Promise<ICategory[]> {
  const { data } = await axiosInstance.get<ICategory[]>('/api/categories');
  return data;
}

export async function approveAiImageOutput(
  generationId: string,
  outputId: string,
  selection: AiImagePublishSelection
): Promise<AiImageMutationResult> {
  const { data } = await axiosInstance.post<AiImageMutationResult>(
    `/api/ai-images/${generationId}/outputs/${outputId}/approve`,
    selection
  );
  return data;
}

export async function rejectAiImageOutput(generationId: string, outputId: string, reason?: string): Promise<AiImageGeneration> {
  const { data } = await axiosInstance.post<AiImageGeneration>(
    `/api/ai-images/${generationId}/outputs/${outputId}/reject`,
    { reason }
  );
  return data;
}

export async function publishAiImageOutput(
  generationId: string,
  outputId: string,
  selection: AiImagePublishSelection
): Promise<AiImageMutationResult> {
  const { data } = await axiosInstance.post<AiImageMutationResult>(
    `/api/ai-images/${generationId}/outputs/${outputId}/publish`,
    selection
  );
  return data;
}

export async function replaceRecipeAssetWithAiImageOutput(
  generationId: string,
  outputId: string,
  targetAssetId: string,
  note?: string
): Promise<AiImageMutationResult> {
  const { data } = await axiosInstance.post<AiImageMutationResult>(
    `/api/ai-images/${generationId}/outputs/${outputId}/replace-recipe-asset`,
    { targetAssetId, note }
  );
  return data;
}

export async function reuseAiImageOutput(generationId: string, outputId: string) {
  const { data } = await axiosInstance.post(`/api/ai-images/${generationId}/reuse`, { outputId });
  return data;
}

export async function deleteAiImageOutput(generationId: string, outputId: string): Promise<AiImageGeneration> {
  const { data } = await axiosInstance.delete<AiImageGeneration>(`/api/ai-images/${generationId}/outputs/${outputId}`);
  return data;
}

export async function deleteAiImageGeneration(generationId: string): Promise<{ deleted: boolean; generationId: string }> {
  const { data } = await axiosInstance.delete<{ deleted: boolean; generationId: string }>(`/api/ai-images/${generationId}`);
  return data;
}

export async function optimizeAiImagePrompt(prompt: string, model: string): Promise<string> {
  const { data } = await axiosInstance.post<{ optimizedPrompt: string }>('/api/ai-images/optimize-prompt', { prompt, model });
  return data.optimizedPrompt;
}

export async function markAiImageCommentsRead(generationId: string, outputId: string): Promise<AiImageGeneration> {
  const { data } = await axiosInstance.post<AiImageGeneration>(
    `/api/ai-images/${generationId}/outputs/${outputId}/mark-read`
  );
  return data;
}

export async function addAiImageComment(generationId: string, outputId: string, text: string): Promise<AiImageGeneration> {
  const { data } = await axiosInstance.post<AiImageGeneration>(
    `/api/ai-images/${generationId}/outputs/${outputId}/comment`,
    { text }
  );
  return data;
}

export async function fetchCommentedOutputCount(): Promise<number> {
  const { data } = await axiosInstance.get<{ count: number }>('/api/ai-images/commented-count');
  return data.count;
}
